// Formatação por idioma. Tamanhos em GB decimal (10^9), como na cobrança.
import { useLocale, type Locale } from "../i18n/core";

const GB = 1e9;

const words: Record<Locale, { never: string; now: string }> = {
  "en-US": { never: "never", now: "just now" },
  "pt-BR": { never: "nunca", now: "agora" },
  es: { never: "nunca", now: "ahora" },
};

export type Format = ReturnType<typeof createFormat>;

export function createFormat(locale: Locale) {
  const nf0 = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
  const nf1 = new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const dtf = new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  const df = new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short" });
  const dfull = new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", year: "numeric" });
  const tf = new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" });
  const wf = new Intl.DateTimeFormat(locale, { weekday: "short", day: "2-digit", month: "short" });
  const mf = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" });
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const lf = new Intl.ListFormat(locale, { style: "long", type: "conjunction" });
  const w = words[locale];
  const day = (iso: string) => new Date(iso.length === 10 ? iso + "T12:00:00" : iso);

  return {
    locale,
    bytes(n: number): string {
      if (n >= 1e12) return `${nf1.format(n / 1e12)} TB`;
      if (n >= GB * 100) return `${nf0.format(n / GB)} GB`;
      if (n >= GB) return `${nf1.format(n / GB)} GB`;
      if (n >= 1e6) return `${nf0.format(n / 1e6)} MB`;
      if (n >= 1e3) return `${nf0.format(n / 1e3)} KB`;
      return `${nf0.format(n)} B`;
    },
    gb: (n: number) => `${nf0.format(n / GB)} GB`,
    int: (n: number) => nf0.format(n),
    pct: (ratio: number) => `${nf0.format(ratio * 100)}%`,
    ratio: (n: number) => `${nf1.format(n)}×`,
    dateTime: (iso: string) => dtf.format(new Date(iso)),
    dayMonth: (iso: string) => df.format(day(iso)).replace(/\./g, ""),
    fullDate: (iso: string) => dfull.format(day(iso)),
    time: (iso: string) => tf.format(new Date(iso)),
    weekdayDate: (iso: string) => wf.format(day(iso)).replace(/\./g, ""),
    monthYear: (iso: string) => mf.format(day(iso)),
    list: (items: string[]) => lf.format(items),
    ago(iso: string | null | undefined, now = Date.now()): string {
      if (!iso) return w.never;
      const s = Math.round((new Date(iso).getTime() - now) / 1000);
      const abs = Math.abs(s);
      if (abs < 60) return w.now;
      if (abs < 3600) return rtf.format(Math.round(s / 60), "minute");
      if (abs < 86400) return rtf.format(Math.round(s / 3600), "hour");
      return rtf.format(Math.round(s / 86400), "day");
    },
    duration(seconds: number): string {
      if (seconds < 60) return `${nf0.format(seconds)} s`;
      if (seconds < 3600) return `${nf0.format(Math.round(seconds / 60))} min`;
      const h = Math.floor(seconds / 3600);
      const m = Math.round((seconds % 3600) / 60);
      return m ? `${nf0.format(h)} h ${m} min` : `${nf0.format(h)} h`;
    },
  };
}

const cache = new Map<Locale, Format>();

export function formatFor(locale: Locale): Format {
  let f = cache.get(locale);
  if (!f) {
    f = createFormat(locale);
    cache.set(locale, f);
  }
  return f;
}

/** Formatação no idioma atual da console. */
export function useFormat(): Format {
  return formatFor(useLocale());
}

export function initials(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "").join("");
}
